/**
 * Estado compartilhado: leitura ↔ edição inline (Enter/Esc, blur, foco).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseInlineEditOptions<T> {
    value: T;
    toDisplayString: (value: T) => string;
    parse: (raw: string) => T | null;
    onCommit: (value: T) => void;
    selectOnFocus?: boolean;
}

export function useInlineEdit<T>({
    value,
    toDisplayString,
    parse,
    onCommit,
    selectOnFocus = false,
}: UseInlineEditOptions<T>) {
    const [isEditing, setIsEditing] = useState(false);
    const [localValue, setLocalValue] = useState(() => toDisplayString(value));
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!isEditing) {
            setLocalValue(toDisplayString(value));
        }
    }, [value, isEditing, toDisplayString]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            if (selectOnFocus) {
                inputRef.current.select();
            }
        }
    }, [isEditing, selectOnFocus]);

    const startEditing = useCallback(() => {
        setIsEditing(true);
        setLocalValue(toDisplayString(value));
    }, [value, toDisplayString]);

    const cancel = useCallback(() => {
        setIsEditing(false);
        setLocalValue(toDisplayString(value));
    }, [value, toDisplayString]);

    const save = useCallback(() => {
        setIsEditing(false);
        const parsed = parse(localValue.trim());
        if (parsed === null) {
            setLocalValue(toDisplayString(value));
            return;
        }
        onCommit(parsed);
    }, [localValue, parse, onCommit, toDisplayString, value]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
        },
        [save, cancel],
    );

    const stopPropagation = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    const endEditing = useCallback(() => {
        setIsEditing(false);
    }, []);

    return {
        isEditing,
        localValue,
        setLocalValue,
        inputRef,
        startEditing,
        cancel,
        save,
        endEditing,
        handleKeyDown,
        stopPropagation,
    };
}
